/**
 * Page scroll locking for full-screen loading overlays.
 *
 * Reference counted because more than one overlay can be alive at a time (nested
 * views, or a route change landing before the outgoing loader is destroyed).
 * The last release restores exactly what was there before the first lock, so a
 * component that is torn down early can never unlock a sibling that is still up.
 */

let lockCount = 0;
let previousOverflow = '';
let previousDocOverflow = '';
let previousPaddingRight = '';
let previousContainerOverflow = '';

/** Freeze body & main container scroll and compensate for the removed scrollbar to avoid a layout shift. */
export function lockPageScroll(): void {
  if (typeof document === 'undefined' || typeof window === 'undefined') return;
  if (lockCount === 0) {
    const { body, documentElement } = document;
    previousOverflow = body.style.overflow;
    previousDocOverflow = documentElement.style.overflow;
    previousPaddingRight = body.style.paddingRight;

    // Width the scrollbar occupied before we hid it.
    const scrollbarWidth = window.innerWidth - documentElement.clientWidth;
    body.style.overflow = 'hidden';
    documentElement.style.overflow = 'hidden';
    if (scrollbarWidth > 0) {
      const current = parseFloat(body.style.paddingRight) || 0;
      body.style.paddingRight = `${current + scrollbarWidth}px`;
    }

    const pageContainer = document.querySelector('.page-container') as HTMLElement | null;
    if (pageContainer) {
      previousContainerOverflow = pageContainer.style.overflow;
      pageContainer.style.overflow = 'hidden';
    }
  }
  lockCount++;
}

/** Release one lock. Only the final release restores the original styles. */
export function unlockPageScroll(): void {
  if (typeof document === 'undefined') return;
  if (lockCount === 0) return;
  lockCount--;
  if (lockCount > 0) return;

  document.body.style.overflow = previousOverflow;
  document.documentElement.style.overflow = previousDocOverflow;
  document.body.style.paddingRight = previousPaddingRight;

  const pageContainer = document.querySelector('.page-container') as HTMLElement | null;
  if (pageContainer) {
    pageContainer.style.overflow = previousContainerOverflow;
  }

  previousOverflow = '';
  previousDocOverflow = '';
  previousPaddingRight = '';
  previousContainerOverflow = '';
}
